import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthService } from 'src/app/services/auth.service';
import { catchError, distinctUntilChanged, finalize, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { DocumentTemplateService as ComponentService } from '../document-template.service';
import { DocumentCategoryService } from '../../document-category/document-category.service';
import { CerereTypeService } from '../../cerere-type/cerere-type.service';
import { NotificationService } from 'src/app/services/notification.service';
import { helper } from 'src/environments/helper';
import {
    DocumentTemplate as objectPayload,
    DocumentTemplateAddPayload as objectAddPayload,
    DocumentTemplateEditPayload as objectEditPayload,
    DocumentTemplateForm as objectForm,
} from '../document-template.interface';

import { AngularEditorConfig } from '@kolkov/angular-editor';
import { initializeEditor } from 'src/app/utils/editorConfig';
import { RegistreService } from '../../registre/registre.service';
import { TemplateCategoriesService, TemplateCategory } from '../../template-categories/template-categories.service';
import Swal from 'sweetalert2';

import { conditionalRequiredValidator } from '../../../../validators/conditional-formating'; // Ensure this path is correct
import { Generic } from 'src/app/interfaces/generic.interface';
import { CommonService } from 'src/app/services/common.service';

@Component({
    selector: 'app-add-document-template',
    templateUrl: './add-document-template.component.html',
    styleUrls: ['./add-document-template.component.scss'],
})
export class AddDocumentTemplateComponent implements OnInit, OnDestroy {
    // general error
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    helper = helper;
    destination = 'toPrimarie';

    editorConfig!: AngularEditorConfig;
    is_address: boolean = false;
    registru_necessary: boolean = false;
    document_generabil_dual: boolean = false;

    token!: string;
    isLoading = false;
    form: FormGroup = new FormGroup({
        name: new FormControl('', [Validators.required]),
        context: new FormControl(''),
        context_cetatean: new FormControl(''),
        type_id: new FormControl(-1, [Validators.required]),
        registru_id: new FormControl('-1'),
        is_address: new FormControl(this.is_address, []),
        registru_necessary: new FormControl(this.registru_necessary, []),
        document_generabil_dual: new FormControl(this.document_generabil_dual, []),
        destination: new FormControl(this.destination, []),
        directie_id: new FormControl(-1, [Validators.required]),
        compartiment_id: new FormControl(-1),
        primary_category_id: new FormControl(-1)
    }, { validators: conditionalRequiredValidator('registru_id', 'registru_necessary', true) });

    cereriTypes$: Generic[] = [];
    registre$: any = [];

    directiiActive: Generic[] = this.data;
    compartimenteActive!: Generic[];

    private addDestroy$ = new Subject<void>();

    filteredDirectii: any[] = this.data;
    directiiControl = new FormControl();

    filteredCompartimente: any[] = [];
    compartimenteControl = new FormControl();

    filteredTipCerere: any[] = [];
    tipCerereControl = new FormControl();

    availableParentCategories: TemplateCategory[] = [];

    filteredParentCategories: any[] = [];
    parentCategoriesControl = new FormControl();

    defaultParameter: { name: string; id: string } = {
        name: '-- Alegeți --',
        id: '-1',
    };

    constructor(
        private dialogRef: MatDialogRef<AddDocumentTemplateComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private componentService: ComponentService,
        private notificationService: NotificationService,
        private documentCategoryService: DocumentCategoryService,
        private authService: AuthService,
        private cerereTypeService: CerereTypeService,
        private registreService: RegistreService,
        private templateCategoriesService: TemplateCategoriesService,
        private commonService: CommonService,
    ) {
        this.processToken();
    }

    ngOnInit(): void {
        this.editorConfig = initializeEditor();
        this.loadDocumentCereriTypes();
        this.loadDocumentRegistre();
        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.addDestroy$.next();
        this.addDestroy$.complete();
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.token = token;
            this.componentService.setToken(token);
        });
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directiiActive;
                    return of(this.directiiActive);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimenteActive;
                    return of(this.compartimenteActive);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.tipCerereControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredTipCerere = this.cereriTypes$;
                    return of(this.cereriTypes$);
                }
                return this.filterTipCerere(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredTipCerere = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.parentCategoriesControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredParentCategories = this.availableParentCategories;
                    return of(this.availableParentCategories);
                }
                return this.filterParentCategories(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredParentCategories = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiActive.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onDirectieSelectClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(directie =>
                directie.id === this.form.get('directie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directiiActive;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.form.controls['compartiment_id'].patchValue(-1);
        this.form.controls['primary_category_id'].patchValue(-1);

        if (directieId == -1) {
            this.compartimenteActive = [];
            this.filteredCompartimente = [];
            this.availableParentCategories = [];
            this.filteredParentCategories = [];
            return;
        }

        if (directieId) {
            this.loadCompartimenteActive(directieId);
            this.loadAvailableParentCategories();
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;
        let compartimenteObservable$ = directieId
            ? this.commonService.getCompartimenteByDirectieId(directieId)
            : this.commonService.getCompartimenteActive();

        compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => (this.isLoading = false))
        ).subscribe({
            next: (res) => {
                this.compartimenteActive = [...res];
                this.filteredCompartimente = [...res];
            },
            error: (err) => {
                console.log(err);
                this.filteredCompartimente = [];
            },
        })
    }

    filterCompartimente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.compartimenteActive.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onCompartimentSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.form.get('compartiment_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimenteActive;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onCompartimentChange(compartimentId: number) {
        this.form.controls['primary_category_id'].patchValue(-1);

        if (compartimentId) {
            this.loadAvailableParentCategories();
        }
    }

    loadAvailableParentCategories(): void {
        const directieId = this.form.get('directie_id')?.value;
        const compartimentId = this.form.get('compartiment_id')?.value !== -1 ? this.form.get('compartiment_id')?.value : null;

        this.templateCategoriesService.getCategoriesByOrganization(directieId, compartimentId)
            .subscribe({
                next: (response) => {
                    if (response.errors === false) {
                        this.availableParentCategories = response.data || [];
                        this.filteredParentCategories = response.data;
                    }
                },
                error: (error) => {
                    console.error('Error loading parent categories:', error);
                    this.availableParentCategories = [];
                }
            });
    }

    filterParentCategories(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.availableParentCategories.filter(category =>
            this.normalizeString(category.name).includes(normalizedSearchTerm)
        ));
    }

    onParentCategoriesSelectClosed() {
        const selectedValue = this.parentCategoriesControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredParentCategories.some(category =>
                category.id === this.form.get('primary_category_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.parentCategoriesControl.setValue(null);
                this.filteredParentCategories = this.availableParentCategories;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    submitForm() {
        this.isLoading = true;

        const {
            name,
            context_cetatean,
            context,
            type_id,
            registru_id,
            is_address,
            registru_necessary,
            document_generabil_dual,
            directie_id,
            compartiment_id,
            primary_category_id,
        } = this.form.value as objectAddPayload;
        const newObject: objectAddPayload = {
            type_id,
            name,
            context_cetatean,
            context,
            registru_id,
            is_address,
            registru_necessary,
            document_generabil_dual,
            directie_id: directie_id,
            compartiment_id: compartiment_id,
            primary_category_id: primary_category_id,
        };
        // console.log(this.form.value);
        // this.isLoading = false
        // return false

        this.componentService
            .store(newObject)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    onAdresaChange(option: any) {
        this.form.get('is_address')?.setValue(option.value);
    }

    onRegistruChange(option: any) {
        this.form.get('registru_necessary')?.setValue(option.value);
        this.form.patchValue({
            registru_id: option.value == false ? '' : this.form.value.registru_id,
            registru_necessary: option.value
        })

        const registru_id = this.form.get('registru_id') as FormControl;

        if (option.value === true) {
            registru_id.setValidators([Validators.required]);
        } else {
            registru_id.patchValue('');
            registru_id.clearValidators();
            registru_id.setErrors(null);
        }

        this.form.updateValueAndValidity();
    }

    onDualChange(option: any) {
        this.form.get('document_generabil_dual')?.setValue(option.value);
        this.form.patchValue({
            destination: option.value == false ? 'toPrimarie' : this.form.value.destination,
            document_generabil_dual: option.value
        })
        if (option.value == false) {
            this.destination = 'toPrimarie';
        }
    }

    onDestinatieChange(option: any) {
        this.destination = option.value;

        const contextTouchedOrDirty =
            this.form.get('context')?.touched || this.form.get('context')?.dirty;
        const contextCetateanTouchedOrDirty =
            this.form.get('context_cetatean')?.touched ||
            this.form.get('context_cetatean')?.dirty;
        const contextHasValue = this.form.get('context')?.value.length > 0;
        const contextCetateanHasValue =
            this.form.get('context_cetatean')?.value.length > 0;

        const shouldConfirmClear =
            (contextTouchedOrDirty && contextHasValue) ||
            (contextCetateanTouchedOrDirty && contextCetateanHasValue);

        const clearContent = () => {
            const patchValues: any = {};
            if (
                option.value === 'toCetatean' &&
                contextCetateanTouchedOrDirty &&
                contextCetateanHasValue
            ) {
                patchValues.context_cetatean = '';
            } else if (
                option.value === 'toPrimarie' &&
                contextTouchedOrDirty &&
                contextHasValue
            ) {
                patchValues.context = '';
            } else if (option.value === 'toate') {
                if (contextTouchedOrDirty && contextHasValue) {
                    patchValues.context = '';
                }
                if (contextCetateanTouchedOrDirty && contextCetateanHasValue) {
                    patchValues.context_cetatean = '';
                }
            }

            this.form.patchValue(patchValues);
            this.destination = option.value;
        };

        // we will decide on this later on the road
        // if (shouldConfirmClear) {
        // 	Swal.fire({
        // 		title: 'Sunteți sigur?',
        // 		text: 'Această acțiune vă șterge conținutul adăugat până la acest moment.',
        // 		icon: 'warning',
        // 		showCancelButton: true,
        // 		confirmButtonColor: '#3085d6',
        // 		cancelButtonColor: '#d33',
        // 		confirmButtonText: 'Da, șterge!',
        // 	}).then((result) => {
        // 		if (result.isConfirmed) {
        // 			clearContent();
        // 			Swal.fire('Șters!', 'Conținutul a fost șters!', 'success');
        // 		}
        // 	});
        // } else {
        // }
    }

    loadDocumentCereriTypes() {
        this.isLoading = true;
        this.cerereTypeService
            .getRecordsList()
            .pipe(
                map((results: any) => [...results]),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.cereriTypes$ = results;
                    this.filteredTipCerere = results;
                },
                error: (error) => {
                    console.error('Error loading tipuri cerere', error);
                    this.filteredTipCerere = [];
                },
            })
    }

    loadDocumentRegistre() {
        this.isLoading = true;
        this.registre$ = this.registreService.getRecordsList().pipe(
            map((results: any) => [this.defaultParameter, ...results]),
            finalize(() => (this.isLoading = false))
        );
    }

    filterTipCerere(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.cereriTypes$.filter(tipCerere =>
            this.normalizeString(tipCerere.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onTipCerereSelectClosed() {
        const selectedValue = this.tipCerereControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredTipCerere.some(tipCerere =>
                tipCerere.id === this.form.get('type_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.tipCerereControl.setValue(null);
                this.filteredTipCerere = this.cereriTypes$;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }
}
